import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Building2, MapPin, DollarSign, Zap, Search, Shield, TrendingUp, Briefcase, Mail, Phone } from "lucide-react";
import { fadeUp, Section, Card, GoldButton } from "../components/shared/UIComponents";
import { usePageNavigate } from "../hooks/usePageNavigate";

const STEPS = [
  { num: "01", title: "Source", desc: "We build proprietary deal flow through direct outreach, broker relationships, and data-driven targeting. Approximately 70% of our pipeline is off-market — properties that never hit a listing service. This gives us access to better pricing, less competition, and motivated sellers who value certainty over auction dynamics." },
  { num: "02", title: "Underwrite", desc: "Every acquisition is stress-tested through rigorous financial modeling, market analysis, and physical due diligence. We use AI-powered tools alongside deep human judgment to evaluate cash flow, renovation costs, exit strategies, and downside scenarios. We do not rely on optimistic projections — we build returns from current fundamentals." },
  { num: "03", title: "Acquire", desc: "We move with speed and certainty. Our standard offer timeline is 48 hours from initial review, with the ability to close in as few as 21 days. We structure transactions as direct purchases, assignments, or joint ventures depending on what best serves the deal and the seller." },
  { num: "04", title: "Grow", desc: "Post-acquisition, we execute value-add business plans through targeted renovations, operational improvements, and professional property management. We optimize for long-term cash flow and asset appreciation — creating returns for our capital partners and lasting value in the communities we invest in." },
];

const MARKETS = ["Houston MSA", "Dallas-Fort Worth", "Austin", "San Antonio", "Phoenix AZ", "Atlanta GA"];

export default function AboutPage() {
  const { navigateTo } = usePageNavigate();

  return (
    <>
      {/* ═══ HERO ═══ */}
      <section className="relative py-20 sm:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-emerald-950 via-[#022c22] to-emerald-950" />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 0L60 30L30 60L0 30z' fill='none' stroke='%23E5C98B' stroke-width='0.5'/%3E%3C/svg%3E")`, backgroundSize: '60px 60px' }} />
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/3 right-0 w-[500px] h-[500px] bg-gold/8 rounded-full blur-[180px]" />
        </div>
        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <h1 className="text-4xl font-bold text-cream sm:text-5xl md:text-6xl lg:text-7xl tracking-tight">About <span className="luxury-text-gradient">Imaani Capital</span></h1>
            <p className="mt-5 max-w-2xl text-base text-cream/70 leading-relaxed sm:text-lg sm:leading-8">
              A commercial real estate investment firm headquartered in Houston, Texas.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ═══ OUR FIRM ═══ */}
      <Section eyebrow="Our Firm" title="Who We Are">
        <div className="grid gap-10 lg:grid-cols-5 lg:gap-16 items-start">
          {/* Left: description — 3 cols */}
          <motion.div {...fadeUp} className="lg:col-span-3 space-y-5">
            <p className="text-sm sm:text-base text-[#444] leading-7 sm:leading-8">
              Imaani Capital is a commercial real estate investment firm specializing in the acquisition of multifamily, retail, and industrial assets across the United States. We are direct buyers — not brokers, not wholesalers — with the capital relationships and operational experience to close transactions with certainty and speed.
            </p>
            <p className="text-sm sm:text-base text-[#444] leading-7 sm:leading-8">
              Headquartered in Houston, Texas, we bring deep knowledge of the Texas and Sun Belt markets combined with the reach to pursue acquisitions nationally. Our focus is multifamily — from 5-unit workforce housing to 200-unit apartment communities — though we actively acquire retail, industrial, and mixed-use assets that meet our investment criteria.
            </p>
          </motion.div>
          {/* Right: stats card — 2 cols */}
          <motion.div {...fadeUp} transition={{ delay: 0.15 }} className="lg:col-span-2">
            <Card className="!p-0 overflow-hidden">
              {[
                { value: "$50M+", label: "Transactions Reviewed" },
                { value: "6", label: "Active Markets" },
                { value: "National", label: "Acquisition Reach" },
                { value: "Houston TX", label: "Headquarters" },
              ].map((s, i) => (
                <div key={i} className={`flex items-center justify-between px-6 py-5 ${i < 3 ? "border-b border-[#EDE8DC]" : ""}`}>
                  <span className="text-xs uppercase tracking-[0.15em] text-[#888] font-semibold">{s.label}</span>
                  <span className="text-lg font-bold text-[#0A0A0A] tracking-tight">{s.value}</span>
                </div>
              ))}
            </Card>
          </motion.div>
        </div>
      </Section>

      {/* ═══ WHY IMAANI ═══ */}
      <section className="bg-cream border-y border-cream-warm" data-testid="why-imaani">
        <div className="mx-auto max-w-3xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8 text-center">
          <motion.div {...fadeUp}>
            <p className="text-xs sm:text-sm uppercase tracking-[0.4em] text-gold font-semibold mb-4">The Name</p>
            <h2 className="text-3xl font-bold tracking-tight text-[#0A0A0A] sm:text-4xl">Why Imaani</h2>
            <div className="mx-auto mt-4 h-0.5 w-12 bg-gold/60" />
            <p className="mt-8 text-sm sm:text-base text-[#444] leading-7 sm:leading-8">
              The name <em>Imaani</em> means <strong>faith</strong> in Swahili and Arabic. We chose it deliberately — because we believe that disciplined, faith-driven investment in real estate creates the kind of generational wealth that endures. Every acquisition we make, every capital partner we work with, and every seller we serve reflects that conviction. We are building something that lasts.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ═══ INVESTMENT FOCUS ═══ */}
      <Section eyebrow="Investment Focus" title="What Defines Us">
        <div className="grid gap-5 sm:grid-cols-2">
          {[
            { icon: Building2, title: "Primary Asset Class", body: "Multifamily — 5 to 500+ units. Workforce housing, value-add, and stabilized apartment communities. This is our core competency and where we deploy the majority of our capital." },
            { icon: MapPin, title: "Geographic Focus", body: "Texas is our home market and primary focus. We actively acquire in all Sun Belt states and pursue opportunities nationally when the fundamentals justify it." },
            { icon: DollarSign, title: "Deal Size", body: "$500K to $50M per transaction. We are flexible on structure — direct purchase, assignment, or joint venture — and can scale to portfolio-level acquisitions." },
            { icon: Zap, title: "Our Edge", body: "Off-market sourcing through proprietary channels. 48-hour written offers. 21-day close capability. We give sellers certainty, speed, and discretion that listed processes cannot match." },
          ].map((c, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}>
              <Card className="h-full !p-7 hover:shadow-[0_8px_30px_rgba(6,95,70,0.08)] hover:border-emerald-200 transition-all">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-100 to-emerald-50 border border-emerald-200 flex items-center justify-center mb-5">
                  <c.icon className="h-5 w-5 text-emerald-700" />
                </div>
                <h3 className="text-base font-bold text-[#0A0A0A] mb-2">{c.title}</h3>
                <p className="text-sm text-[#555] leading-relaxed">{c.body}</p>
              </Card>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* ═══ OUR APPROACH ═══ */}
      <section className="relative py-16 sm:py-24 overflow-hidden" data-testid="approach-section">
        <div className="absolute inset-0 bg-gradient-to-b from-emerald-950 via-[#022c22] to-emerald-950" />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M20 0L40 20L20 40L0 20z' fill='none' stroke='%23E5C98B' stroke-width='0.5'/%3E%3C/svg%3E")`, backgroundSize: '40px 40px' }} />
        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="mb-10 sm:mb-14">
            <p className="text-xs sm:text-sm uppercase tracking-[0.4em] text-gold font-semibold">Our Process</p>
            <h2 className="mt-3 text-3xl font-bold tracking-tight text-cream sm:text-4xl md:text-5xl">How We Operate</h2>
          </motion.div>
          <div className="grid gap-6 sm:gap-8 lg:grid-cols-2">
            {STEPS.map((s, i) => (
              <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.08 }}
                className="rounded-2xl border border-emerald-700/30 bg-emerald-900/30 backdrop-blur-sm p-6 sm:p-8 hover:border-gold/30 transition-colors">
                <div className="flex items-center gap-4 mb-4">
                  <span className="text-2xl font-black text-gold/40 font-mono">{s.num}</span>
                  <h3 className="text-xl font-bold text-cream">{s.title}</h3>
                </div>
                <p className="text-sm text-cream/70 leading-7">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ LEADERSHIP ═══ */}
      <Section eyebrow="Leadership" title="Our Team">
        <div className="max-w-3xl">
          <div className="flex flex-col sm:flex-row items-start gap-6 sm:gap-8">
            {/* Avatar placeholder */}
            <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-full bg-gradient-to-br from-emerald-800 to-emerald-900 border-2 border-emerald-700 flex items-center justify-center shrink-0 shadow-lg">
              <span className="text-3xl sm:text-4xl font-bold text-cream/80">IC</span>
            </div>
            <div>
              <h3 className="text-xl font-bold text-[#0A0A0A] tracking-tight">Founder & Managing Principal</h3>
              <p className="mt-1 text-sm text-emerald-700 font-semibold">Imaani Capital</p>
              <p className="mt-4 text-sm text-[#555] leading-7">
                Imaani Capital was founded to bring institutional-quality discipline to middle-market commercial real estate acquisitions. Our leadership team combines direct acquisition experience across multifamily, retail, and industrial assets with deep relationships in the Texas and Sun Belt markets. We are hands-on operators who underwrite every deal personally and maintain direct involvement through closing and asset management.
              </p>
            </div>
          </div>
          <div className="mt-8 pt-6 border-t border-[#EDE8DC]">
            <p className="text-sm text-[#888] leading-relaxed italic">
              Imaani Capital is supported by an experienced network of Texas real estate professionals, capital market specialists, and legal advisors.
            </p>
          </div>
          {/* Note for photo replacement */}
          {/* To add a founder photo, replace the avatar above with: <img src="/assets/team/founder.jpg" alt="Founder" className="w-24 h-24 sm:w-28 sm:h-28 rounded-full object-cover border-2 border-emerald-700 shadow-lg" /> */}
        </div>
      </Section>

      {/* ═══ OUR MARKETS ═══ */}
      <section className="bg-cream border-y border-cream-warm" data-testid="markets-section">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-16 lg:px-8 text-center">
          <motion.div {...fadeUp}>
            <p className="text-xs sm:text-sm uppercase tracking-[0.4em] text-gold font-semibold mb-3">Where We Operate</p>
            <h2 className="text-2xl font-bold tracking-tight text-[#0A0A0A] sm:text-3xl">Our Markets</h2>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              {MARKETS.map((m, i) => (
                <motion.span key={i} initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[#EDE8DC] bg-white text-sm font-semibold text-[#333] shadow-sm hover:border-emerald-300 hover:shadow-md transition-all">
                  <MapPin className="h-3.5 w-3.5 text-emerald-600" /> {m}
                </motion.span>
              ))}
            </div>
            <p className="mt-6 text-sm text-[#999] italic">Actively pursuing acquisitions nationally.</p>
          </motion.div>
        </div>
      </section>

      {/* ═══ CONTACT CTA ═══ */}
      <section className="relative py-16 sm:py-24 overflow-hidden" data-testid="about-contact-cta">
        <div className="absolute inset-0 bg-gradient-to-b from-emerald-950 via-[#022c22] to-emerald-950" />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M20 0L40 20L20 40L0 20z' fill='none' stroke='%23E5C98B' stroke-width='0.5'/%3E%3C/svg%3E")`, backgroundSize: '40px 40px' }} />
        <div className="relative z-10 mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fadeUp}>
            <h2 className="text-2xl font-bold text-cream sm:text-3xl md:text-4xl luxury-text-gradient">Ready to Talk?</h2>
            <p className="mt-5 text-base text-cream/70 leading-7 sm:text-lg">
              Ready to discuss a property or investment opportunity? We respond within 24 hours.
            </p>
            <div className="mt-8 flex flex-wrap gap-3 justify-center sm:gap-5">
              <GoldButton onClick={() => navigateTo("sell")} className="!px-6 !py-3 sm:!px-8 sm:!py-4 !text-xs sm:!text-sm" data-testid="about-cta-sell">
                Submit a Property <ArrowRight className="h-3.5 w-3.5 ml-1 inline" />
              </GoldButton>
              <GoldButton onClick={() => navigateTo("contact")} variant="secondary" className="!px-6 !py-3 sm:!px-8 sm:!py-4 !text-xs sm:!text-sm" data-testid="about-cta-contact">
                Contact Our Team
              </GoldButton>
            </div>
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-10">
              <a href="mailto:connect@imaanicapital.com" className="flex items-center gap-2 text-sm text-cream/60 hover:text-gold transition-colors">
                <Mail className="h-4 w-4" /> connect@imaanicapital.com
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
}
